"use client";
// contexts/AccessTokenContext.jsx
import { createContext, useState, useEffect, useMemo, useContext } from "react";
import { userDataConnect } from "../data_connect/userDataConnect";
import { useSyncExternalStore } from "react";
import {
    getMemoryAccessToken,
    setMemoryAccessToken,
    subscribeMemoryAccessToken,
    memoryAccessToken,
} from "../stores/authStorage";

const AuthValueContext = createContext();
const AuthActionsContext = createContext();

export function AuthContextProvider({ children }) {
    const accessToken = useSyncExternalStore(
        subscribeMemoryAccessToken, // 구독 함수
        getMemoryAccessToken, // 값 가져오는 함수
        () => memoryAccessToken, // 서버 스냅샷 함수 (SSR 시)
    );
    const [isReady, setReady] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const reissueAccessTokenResponse =
                    await userDataConnect().reissueAccessToken();
                const token =
                    reissueAccessTokenResponse.data?.data?.accessToken;
                setMemoryAccessToken(token);
            } catch {
                setMemoryAccessToken(null);
            } finally {
                setReady(true);
            }
        })();
    }, []);

    const actions = useMemo(() => {
        return {
            login(accessToken) {
                setMemoryAccessToken(accessToken);
            },
            logout() {
                setMemoryAccessToken(null);
            },
            setReady(bool) {
                setReady(bool);
            },
        };
    }, []);
    return (
        <AuthActionsContext.Provider value={actions}>
            <AuthValueContext.Provider
                value={{
                    isLoggedIn: !!accessToken,
                    isReady,
                    accessToken: accessToken,
                }}
            >
                {children}
            </AuthValueContext.Provider>
        </AuthActionsContext.Provider>
    );
}

export function useAuthContextValueHook() {
    const value = useContext(AuthValueContext);

    if (value === undefined) {
        throw new Error(
            "useAuthContextValueHook should be used within AuthContextProvider",
        );
    }

    return {
        isLoggedIn: value.isLoggedIn,
        isReady: value.isReady,
        accessToken: value.accessToken,
    };
}

export function useAuthContextActionsHook() {
    const value = useContext(AuthActionsContext);

    if (value === undefined) {
        throw new Error(
            "useAuthContextActionsHook should be used within AuthContextProvider",
        );
    }

    return {
        login: value.login,
        logout: value.logout,
        setReady: value.setReady,
    };
}
